import type { AlignmentFlag } from './api';

/**
 * Pure "how does the alignment-review deck GROUP its flags into collapsible
 * sections" decision — kept framework-free (no React), same house idiom as
 * alignmentReviewDisplay.ts / refineThreadsSource.ts, so the CREW-UX pass's
 * new by-role grouping is directly unit-testable rather than living only as
 * inline JSX/reduce calls in AlignmentReviewDeck.tsx.
 *
 * THE PROBLEM THIS MODULE SOLVES (the owner, verbatim): "the crew deck renders
 * ALL flags as one long uncollapsed wall — cross-filed cards + near-duplicate
 * merge cards mixed, many per role" — a daunting scroll that invites give-up.
 * The fix groups BY ROLE ("how he thinks about his history"), because a role
 * is the natural unit he already understands — company + title he lived, not
 * an abstract signal-kind bucket a user has never had to think in.
 *
 * Grouping key: `flag.currentRole?.id ?? null` (an unattached achievement has
 * no currentRole — `AlignmentReviewCard.tsx`'s own `filedUnder` already
 * treats this as the honest "Unattached" case, so this module mirrors it
 * rather than inventing a different bucket rule). Order is stable and
 * grounded in the data the deck already has: groups are emitted in the order
 * their FIRST flag appears in the input array (never re-sorted by count or
 * alphabetically — that would imply a ranking the product doesn't make).
 */

export interface AlignmentReviewSection {
  /** The grouping key: the role's id, or null for the "Unattached" bucket. */
  roleId: string | null;
  /** Real, verbatim role summary from the first flag carrying this roleId
   *  (every flag sharing a roleId carries the SAME currentRole payload, since
   *  it's the server's own summary of that role — never re-derived). Null
   *  only for the unattached bucket. */
  roleLabel: { company: string; title: string } | null;
  flags: AlignmentFlag[];
}

/** Group flags by their current role (company+title), preserving each
 *  section's first-seen order and each flag's original relative order within
 *  its section. Cross-filed cards (a flag whose signals include
 *  cross_employer_leakage) and near-duplicate merge cards (near_duplicate
 *  signal) both group by the SAME rule — "where is this filed right now" —
 *  since that is the honest, single grouping dimension every flag already
 *  carries, regardless of which signal(s) tripped it. */
export function groupAlignmentFlagsByRole(flags: AlignmentFlag[]): AlignmentReviewSection[] {
  const order: (string | null)[] = [];
  const byKey = new Map<string | null, AlignmentReviewSection>();

  for (const flag of flags) {
    const roleId = flag.currentRole?.id ?? null;
    let section = byKey.get(roleId);
    if (!section) {
      section = {
        roleId,
        roleLabel: flag.currentRole ? { company: flag.currentRole.company, title: flag.currentRole.title } : null,
        flags: [],
      };
      byKey.set(roleId, section);
      order.push(roleId);
    }
    section.flags.push(flag);
  }

  return order.map((key) => byKey.get(key)!);
}

/** The section header's honest count chip copy — "1 to look at" / "4 to look
 *  at", never a bare number standing alone without the framing that makes it
 *  legible as "this is the size of the remaining task," not a score. */
export function sectionCountLabel(count: number): string {
  return `${count} to look at`;
}

/** A stable, human key for a section's UI state (which section is expanded) —
 *  the unattached bucket needs a real key too (roleId is null there), so this
 *  is the ONE place both the grouping module and the component agree on how
 *  to key a section, rather than each re-deriving `roleId ?? 'unattached'`
 *  independently. */
export function sectionKey(roleId: string | null): string {
  return roleId ?? '__unattached__';
}
